/**
 * Every trading cycle this account has run, active and finished.
 *
 * They were two screens. The roster lived at `/app/slots` and the closed
 * record at `/app/history`, and when the navigation was rebuilt the second one
 * lost its entry — the route stayed reachable by address and by nothing else,
 * which is how a delivered section disappears without anybody deleting it.
 *
 * One screen, because they are one subject seen at two moments. A cycle is
 * active until it closes and finished afterwards; asking "what is running" and
 * "what has run" from two different places forces the reader to know which
 * question they are asking before they can look.
 *
 * The tab lives in the address. A filtered record is a place a person links to
 * and comes back to, and a tab kept only in memory is neither.
 */
import { PageHeader } from "@/design-system/PageHeader";
import { Button } from "@/design-system/Button";
import { useSearchParams } from "react-router";

import { ErrorState, Forbidden, Loading, NotObserved } from "@/design-system/states/States";
import type { OverviewResponse } from "@/shared/api";
import { CycleBook } from "@/shared/cycles/CycleBook";
import { LiveCycleSummary } from "@/shared/cycles/LiveCycleSummary";
import { useOverview } from "@/shared/overview/useOverview";

import { TradesTerminal } from "@/shared/terminal/TradesTerminal";

import { ClosedCycles } from "./ClosedCycles";

const VIEWS = [
  { id: "terminal", label: "Терминал" },
  { id: "active", label: "Таблица" },
  { id: "closed", label: "Завершённые" },
] as const;

type View = (typeof VIEWS)[number]["id"];

/** The tab in the address, defaulting to what is running now. */
export function readView(params: URLSearchParams): View {
  const view = params.get("view");
  return view === "closed" || view === "active" ? view : "terminal";
}

export function CyclesPage(): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const view = readView(params);

  return (
    <div className="flex min-w-0 flex-col gap-2" data-testid="cycles-page">
      <PageHeader
        title="Циклы"
        description="Что торгуется сейчас и что уже закрыто: один цикл от входа до выхода."
      />

      <div
        role="tablist"
        aria-label="Циклы"
        data-testid="cycles-tabs"
        className="flex w-fit gap-0.5 rounded-row bg-background-base p-0.5"
      >
        {VIEWS.map((option) => (
          <Button
            key={option.id}
            variant="ghost"
            size="sm"
            selected={option.id === view}
            role="tab"
            aria-selected={option.id === view}
            data-testid={`cycles-tab-${option.id}`}
            onClick={() => {
              const next = new URLSearchParams(params);
              if (option.id === "terminal") {
                next.delete("view");
                // The closed record's own controls belong to it. Carrying a
                // period or a symbol filter onto the roster would apply them
                // to rows they do not govern.
                for (const key of ["preset", "since", "until", "symbol"]) {
                  next.delete(key);
                }
              } else {
                next.set("view", option.id);
              }
              setParams(next);
            }}
            className="min-h-8 px-3 text-label-caps"
          >
            {option.label}
          </Button>
        ))}
      </div>

      {view === "closed" ? <ClosedCycles /> : view === "active" ? <Roster /> : <Terminal />}
    </div>
  );
}

/**
 * The roster, read from the same overview query the rest of the app uses.
 *
 * Composed here rather than inside the table for the same reason the market
 * block is composed by the app: a feature that fetches for itself is a feature
 * that cannot be placed anywhere else.
 */
/** Slots the schedule allows that nothing occupies; absent with the ceiling. */
function free(overview: OverviewResponse): number | undefined {
  return overview.idle_slots;
}

/** The terminal: the money line, the chosen cycle's chart, every open cycle. */
function Terminal(): React.JSX.Element {
  const state = useOverview();
  if (state.status === "loading") return <Loading title="Читаю циклы…" />;
  if (state.status === "forbidden") return <Forbidden />;
  if (state.status === "not-observed") return <NotObserved />;
  if (state.status === "unreadable") {
    return (
      <ErrorState
        title="Не могу прочитать циклы"
        detail="Данные не пришли. Позиции и ордера на бирже от этого экрана не зависят."
      />
    );
  }
  return <TradesTerminal overview={state.overview} />;
}

function Roster(): React.JSX.Element {
  const state = useOverview();

  if (state.status === "loading") return <Loading title="Читаю циклы…" />;
  if (state.status === "forbidden") return <Forbidden />;
  if (state.status === "not-observed") return <NotObserved />;
  if (state.status === "unreadable") {
    return (
      <ErrorState
        title="Не могу прочитать циклы"
        detail="Данные не пришли. Позиции и ордера на бирже от этого экрана не зависят."
      />
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-2" data-testid="slots-page">
      {/* The same line Обзор leads with, without its link: there the book is
          nine regions further down, here it is the next block. Counts before
          rows, because "nineteen running, four of them losing" is the reading
          an operator takes from the roster, and finding it by counting red
          numbers down a column is doing the table's work. */}
      <LiveCycleSummary
        slots={state.overview.cycles}
        total={false}
        freeSlots={free(state.overview)}
      />
      {/* The same book Обзор carries, at full height. It was a flatter table
          of its own here — eight columns, no expansion — so the screen named
          after cycles said less about one than the summary did. Uncapped
          because on this screen the book is the subject rather than one region
          among ten. */}
      <CycleBook
        slots={state.overview.cycles}
        title={`Активные · ${String(state.overview.cycles.length)}`}
        capitalLimit={
          state.overview.performance.capital?.target_slot_capital ?? null
        }
        asOf={state.overview.as_of}
        ageSeconds={state.overview.age_seconds}
        stale={state.overview.stale}
      />
    </div>
  );
}
